'use client'

/**
 * Biometric Provider
 * Wraps the ethbridge-sdk BridgeProvider with app-specific configuration
 * ETH Pass Portal - Federal Government of Ethiopia
 */
import { type ReactNode, useMemo } from 'react'

import { type BridgeClientOptions, BridgeProvider } from '@ethbridge/sdk/react'

/**
 * Bridge configuration for production use.
 * Connects to the Iron Bridge (eth-pass-bridge-system) via WebSocket.
 * Default: ws://127.0.0.1:4500/bridge (Iron Bridge MSI installation)
 */
const BRIDGE_CONFIG: BridgeClientOptions = {
  connection: {
    url: process.env['NEXT_PUBLIC_BRIDGE_URL'] || 'ws://127.0.0.1:4500/bridge',
    connectTimeoutMs: 10000,
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectBaseDelayMs: 1000,
    reconnectMaxDelayMs: 10000,
    reconnectBackoffMultiplier: 2,
  },
  retry: {
    maxAttempts: 3,
    baseDelayMs: 500,
    maxDelayMs: 5000,
    backoff: 'exponential',
  },
  logging: {
    level: process.env.NODE_ENV === 'development' ? 'debug' : 'warn',
  },
}

interface BiometricProviderProps {
  children: ReactNode
  /** Whether to auto-connect to the bridge on mount */
  autoConnect?: boolean
  /** Custom bridge URL override */
  bridgeUrl?: string
}

/**
 * BiometricProvider wraps the application with ethbridge-sdk context.
 *
 * This provider should be placed in the layout of pages that need biometric capture.
 * It manages the WebSocket connection to the Rust backend.
 *
 * @example
 * ```tsx
 * <BiometricProvider autoConnect={false}>
 *   <EnrollmentPage />
 * </BiometricProvider>
 * ```
 */
export function BiometricProvider({
  children,
  autoConnect = false,
  bridgeUrl,
}: BiometricProviderProps) {
  // Memoize config to prevent BridgeProvider re-renders
  const config = useMemo<BridgeClientOptions>(() => {
    const finalUrl = bridgeUrl || process.env['NEXT_PUBLIC_BRIDGE_URL'] || 'ws://127.0.0.1:4500/bridge'
    console.log('[BiometricProvider] WebSocket URL:', finalUrl)
    console.log('[BiometricProvider] autoConnect:', autoConnect)

    if (bridgeUrl) {
      return {
        ...BRIDGE_CONFIG,
        connection: { ...BRIDGE_CONFIG.connection!, url: bridgeUrl },
      }
    }
    return BRIDGE_CONFIG
  }, [bridgeUrl])

  return (
    <BridgeProvider options={config} autoConnect={autoConnect}>
      {children}
    </BridgeProvider>
  )
}

export default BiometricProvider
